import { formatDayParts, toDateInputValue } from '@/shared/lib/dates';
import type { DateNeighborhoodItem, Id } from '@/shared/types/domain';

export interface NeighborhoodDay {
  /** `2026-03-14`, no fuso da igreja — a chave de agrupamento e a do React. */
  key: string;
  /** `14/mar` — dia e mês ABREVIADO, ao contrário do intervalo da janela. */
  label: string;
  /** `sáb` */
  weekday: string;
  /** O dia do projeto que está sendo analisado. */
  isCurrentDay: boolean;
  items: readonly DateNeighborhoodItem[];
}

/**
 * A vizinhança da data agrupada por **DIA, não por item** (decisão 20).
 *
 * O nó é o dia porque é essa a pergunta que a faixa responde: "o que mais tem
 * neste sábado?". Com um nó por item, dois projetos no mesmo sábado apareciam
 * como dois pontos da linha do tempo, e o revisor tinha de reconstruir no olho
 * que eram o mesmo dia.
 *
 * **O dia do próprio projeto entra sempre**, mesmo quando a janela não trouxe
 * mais nada e mesmo que ele não venha na lista. Antes, `items.length <= 1`
 * escondia a faixa inteira, e o revisor não distinguia "semana livre" de "não
 * carregou" — que é a diferença entre confiar e não confiar no alerta.
 *
 * Agrupa pelo dia de INÍCIO. Projeto de vários dias aparece uma vez, no dia em
 * que começa: a faixa é orientação de calendário, não ocupação de espaço — quem
 * responde por sobreposição é o alerta de conflito, que olha a janela inteira.
 */
export function groupNeighborhoodByDay(
  items: readonly DateNeighborhoodItem[],
  current: { id: Id; startsAt: string | null },
): readonly NeighborhoodDay[] {
  const currentKey = current.startsAt ? toDateInputValue(current.startsAt) : null;
  const byKey = new Map<string, DateNeighborhoodItem[]>();

  for (const item of items) {
    const key = toDateInputValue(item.starts_at);
    const bucket = byKey.get(key);
    if (bucket) bucket.push(item);
    else byKey.set(key, [item]);
  }

  // O dia do projeto atual existe nem que esteja vazio — ver o docblock.
  if (currentKey && !byKey.has(currentKey)) byKey.set(currentKey, []);

  return [...byKey.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, dayItems]) => {
      const reference = dayItems[0]?.starts_at ?? current.startsAt;
      const parts = reference
        ? formatDayParts(reference)
        : { day: '', month: '', weekday: '' };

      return {
        key,
        label: `${parts.day}/${parts.month}`,
        weekday: parts.weekday,
        isCurrentDay: key === currentKey,
        items: dayItems,
      };
    });
}

/** Nada além do próprio projeto na janela — a nota "Nada mais neste período". */
export function isNeighborhoodEmpty(
  days: readonly NeighborhoodDay[],
  currentProjectId: Id,
): boolean {
  return days.every((day) => day.items.every((item) => item.id === currentProjectId));
}
